import { UserWithMediaAndGroups } from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import { Box, Button, Flex, Heading, Text } from "@chakra-ui/react";
import { useCallback, useRef, useState } from "react";
import { isEmail } from "validator";
import { apiReq } from "../../lib/apiReq";
import { getApiErrorMessage } from "../../lib/error-translators/getApiErrorMessage";
import { store } from "../../lib/store/store";
import { ActionType } from "../../lib/store/storeActions";
import { VerificationCode } from "../VerificationCode/VerificationCode";
import { EmailErrors } from "./AuthenticationDialogTypes";

interface OTPScreenProps {
  onPrevious: (error?: EmailErrors) => void;
  onNext: (user: UserWithMediaAndGroups) => void;
  onAuthenticated?: () => void | Promise<void>;
  email: string;
  source?: string;
  confirmLabel: string;
}

type OTPErrors = {
  email?: string;
  code?: string;
};

/** Why the API refused the code. */
const VERIFY_ERRORS: Record<string, string> = {
  INVALID_TOKEN: "That code didn't match. Double check it and try again.",
};

export const OTPScreen = ({
  onPrevious,
  onNext,
  onAuthenticated,
  email,
  source,
  confirmLabel,
}: OTPScreenProps) => {
  const verifyForm = useRef<HTMLFormElement>(null);
  const [verifyErrors, setVerifyErrors] = useState<OTPErrors>({});
  const [verifyRequest, verifyRequestState] = useReq<{
    token: string;
    user: UserWithMediaAndGroups;
  }>(apiReq);
  const [otp, setOtp] = useState("");
  const verify = useCallback(
    async (otpOverride?: string) => {
      const errors: OTPErrors = {};
      if (!email) {
        errors.email = "Please enter your email";
      } else if (!isEmail(email)) {
        errors.email = "Please double check your email";
      }

      const otpVal = otpOverride || otp;

      if (!otpVal) {
        errors.code = "Please enter your code";
      }

      if (errors.email) {
        onPrevious({ email: errors.email });
      } else if (errors.code) {
        setVerifyErrors(errors);
      } else {
        verifyRequestState.clearError();
        const response = await verifyRequest.post(
          `/user/verify${source ? `?source=${source}` : ""}`,
          {
            email,
            code: otpVal,
          }
        );

        if (response.success) {
          const { result } = response;
          store.dispatch({
            type: ActionType.Login,
            params: result,
          });
          await onAuthenticated?.();
          onNext(result.user);
        }
      }
    },
    [
      email,
      otp,
      onPrevious,
      verifyRequestState,
      verifyRequest,
      source,
      onAuthenticated,
      onNext,
    ]
  );
  const onOtpChange = useCallback(
    (otpVal: string) => {
      setOtp(otpVal);
      if (otpVal.length === 6) {
        verify(otpVal);
      }
    },
    [verify]
  );
  return (
    <Flex
      as="form"
      ref={verifyForm as unknown as React.RefObject<HTMLDivElement>}
      flex={1}
      px="0.25rem"
      direction="column"
      onSubmit={(e: React.FormEvent<Element>) => {
        e.preventDefault();
        verify();
      }}
    >
      <Flex
        flex={1}
        direction="column"
        align="center"
        justify="center"
        gap="1rem"
      >
        <Heading as="h2" size="xl">
          Verification Code
        </Heading>
        <Text fontSize="md" textAlign="center" color="fg.muted">
          Please check your email and enter the code that was sent to you.
        </Text>
        {(verifyErrors.code || verifyRequestState.error) && (
          <Box
            p="3"
            bg="red.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor="red.500"
          >
            <Text color="red.700" fontWeight="700">
              There was problem validating the code
            </Text>
            <Text color="red.700" fontSize="sm">
              {verifyErrors.code ||
                (verifyRequestState.error &&
                  getApiErrorMessage(verifyRequestState.error, VERIFY_ERRORS))}
            </Text>
          </Box>
        )}
        <VerificationCode value={otp} onChange={onOtpChange} length={6} />
      </Flex>
      <Flex
        py="2rem"
        direction={{ base: "column-reverse", sm: "row" }}
        gap="0.65rem"
      >
        <Button
          variant="ghost"
          flex={1}
          onClick={() => onPrevious()}
          type="button"
          disabled={verifyRequestState.loading}
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          colorPalette="greenDark"
          flex={2}
          type="submit"
          size="lg"
          minH="44px"
          disabled={verifyRequestState.loading}
        >
          {confirmLabel}
        </Button>
      </Flex>
    </Flex>
  );
};
